import { useState, useContext, useCallback } from 'react';
import { UserContext } from '../context/user/UserContext';
import { useAuth } from '../features/auth/hooks/useAuth';
import { toast } from 'sonner';

/**
 * Hook to check if a user is verified and show the verification modal if not.
 * 
 * Usage:
 * const { withVerificationCheck, VerificationModal } = useVerificationCheck();
 * 
 * const handleAction = withVerificationCheck(() => {
 *   // actual action logic here
 * });
 * 
 * return (
 *   <>
 *     <button onClick={handleAction}>Action</button>
 *     <VerificationModal />
 *   </>
 * );
 */
export const useVerificationCheck = () => {
  const { user } = useContext(UserContext);
  const { resendVerification } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResend = async (shouldClose = true) => {
    setLoading(true);
    try {
      await resendVerification();
      toast.success("Verification link sent! Please check your email.");
      if (shouldClose) {
        setIsModalOpen(false);
      }
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const checkVerification = useCallback(() => {
    if (user && !user.is_email_verified) {
      setIsModalOpen(true);
      return false;
    }
    return true;
  }, [user]);

  const withVerificationCheck = (action) => {
    return (...args) => {
      if (checkVerification()) {
        return action(...args);
      }
    };
  };

  return {
    isModalOpen,
    setIsModalOpen,
    checkVerification,
    withVerificationCheck,
    handleResend,
    loading
  };
};
